import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock,
  Plus,
  Trash2,
  Loader2,
  ShieldCheck,
  CheckCircle2,
  Users,
  Printer,
  Upload,
  Send,
} from "lucide-react";
import { FadeUp, Overline } from "@/components/Reveal";
import { SignaturePad } from "@/components/SignaturePad";
import { LEGAL_SECTIONS } from "@/components/LegalTexts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/10 bg-carddark px-4 py-3 text-sm text-neutral-100 placeholder:text-neutral-500 transition-colors duration-300 focus:border-neon focus:outline-none focus:ring-1 focus:ring-neon";
const labelCls =
  "mb-2 block font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-400";

const CONSENTS = [
  { key: "terms", label: "Am citit și accept Termenii și condițiile de participare" },
  { key: "entry_agreement", label: "Semnez Acordul de înscriere în competiție" },
  { key: "own_responsibility", label: "Semnez Declarația pe proprie răspundere (sunt apt medical, particip pe propriul risc)" },
  { key: "gdpr", label: "Am citit informarea GDPR și consimt la prelucrarea datelor mele personale" },
];

const PARTICIPATION_TEXT =
  "Mă angajez ferm să particip la competiție în locul și la data care vor fi comunicate de organizatori. Înțeleg că o retragere după semnare afectează organizarea competiției și că trebuie anunțată din timp. Pentru jucătorii sub 16 ani, angajamentul este asumat de părinte/tutore.";

const emptyPlayer = () => ({
  full_name: "",
  birth_date: "",
  phone: "",
  email: "",
  locality: "",
  id_series: "",
  cnp: "",
  signature: "",
  consents: {
    terms: false,
    entry_agreement: false,
    own_responsibility: false,
    gdpr: false,
    participation: false,
  },
  guardian_name: "",
  guardian_relation: "",
  guardian_phone: "",
  guardian_cnp: "",
  guardian_signature: "",
});

const ageFrom = (iso) => {
  if (!iso || iso.length !== 10) return null;
  const b = new Date(iso);
  if (Number.isNaN(b.getTime())) return null;
  const now = new Date();
  let a = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) a -= 1;
  return a;
};

const ClosedNotice = () => (
  <div className="mx-auto flex min-h-[70svh] max-w-2xl items-center px-4">
    <div
      data-testid="cupa-registration-closed"
      className="w-full rounded-3xl border border-white/10 bg-surface p-10 text-center"
    >
      <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-carddark text-flame">
        <Lock className="h-6 w-6" />
      </span>
      <h1 className="mt-6 font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">
        Înscrierile nu sunt deschise
      </h1>
      <p className="mt-4 text-sm leading-relaxed text-neutral-400">
        Formularul oficial de înscriere, cu semnătură electronică, se deschide
        când stabilim data și detaliile turneului. Până atunci, lasă-ne datele pe
        pagina cupei și te anunțăm primul.
      </p>
      <Link
        to="/cupa-caritabila"
        data-testid="cupa-closed-back-link"
        className="mt-8 inline-flex rounded-full bg-neon px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-canvas transition-colors hover:bg-white"
      >
        Înapoi la pagina cupei
      </Link>
    </div>
  </div>
);

const LegalAccordion = () => (
  <div data-testid="cupa-legal-accordion" className="mt-6 space-y-2">
    {LEGAL_SECTIONS.map((s) => (
      <details
        key={s.id}
        data-testid={`cupa-legal-${s.id}`}
        className="group rounded-2xl border border-white/10 bg-carddark px-5 py-4"
      >
        <summary className="cursor-pointer list-none font-display text-sm font-bold uppercase tracking-wide text-white transition-colors group-open:text-neon">
          {s.title}
        </summary>
        <div className="mt-4 space-y-3">
          {s.body.map((p, i) => (
            <p key={i} className="text-xs leading-relaxed text-neutral-400">
              {p}
            </p>
          ))}
        </div>
      </details>
    ))}
  </div>
);

const PlayerCard = ({ index, player, onChange, onRemove, canRemove, mode }) => {
  const paper = mode === "hartie";
  const age = ageFrom(player.birth_date);
  const isMinor = age !== null && age < 18;
  const set = (key) => (e) => onChange(index, { ...player, [key]: e.target.value });
  const setSig = (key) => (val) => onChange(index, { ...player, [key]: val });
  const toggleConsent = (key) => () =>
    onChange(index, { ...player, consents: { ...player.consents, [key]: !player.consents[key] } });

  return (
    <div
      data-testid={`cupa-player-card-${index}`}
      className="rounded-3xl border border-white/10 bg-surface p-6 sm:p-8"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-display text-base font-black uppercase tracking-wide text-white">
          Jucător {index + 1}
          {age !== null && (
            <span className="ml-3 font-mono text-[10px] font-normal tracking-[0.2em] text-neon">
              {age} ani{isMinor ? " · minor" : ""}
            </span>
          )}
        </h3>
        {canRemove && (
          <button
            type="button"
            data-testid={`cupa-remove-player-${index}`}
            onClick={() => onRemove(index)}
            className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 transition-colors hover:text-flame"
          >
            <Trash2 className="h-3 w-3" /> Șterge
          </button>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label className={labelCls}>Nume complet</label>
          <input
            data-testid={`cupa-player-${index}-name`}
            type="text"
            required
            value={player.full_name}
            onChange={set("full_name")}
            placeholder="Nume și prenume"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Data nașterii</label>
          <input
            data-testid={`cupa-player-${index}-birthdate`}
            type="date"
            required
            value={player.birth_date}
            onChange={set("birth_date")}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Localitate</label>
          <input
            data-testid={`cupa-player-${index}-locality`}
            type="text"
            required
            value={player.locality}
            onChange={set("locality")}
            placeholder="Ex: Alba Iulia"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Telefon</label>
          <input
            data-testid={`cupa-player-${index}-phone`}
            type="tel"
            value={player.phone}
            onChange={set("phone")}
            placeholder="07xx xxx xxx"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input
            data-testid={`cupa-player-${index}-email`}
            type="email"
            value={player.email}
            onChange={set("email")}
            placeholder="nume@email.ro"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Serie și număr CI</label>
          <input
            data-testid={`cupa-player-${index}-idseries`}
            type="text"
            value={player.id_series}
            onChange={set("id_series")}
            placeholder="Ex: AX 123456"
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>CNP</label>
          <input
            data-testid={`cupa-player-${index}-cnp`}
            type="text"
            inputMode="numeric"
            maxLength={13}
            value={player.cnp}
            onChange={set("cnp")}
            placeholder="13 cifre"
            className={inputCls}
          />
        </div>
      </div>

      {!paper && (
        <>
          <div className="mt-7 space-y-3">
            {CONSENTS.map((c) => (
              <label
                key={c.key}
                className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-neutral-300"
              >
                <input
                  data-testid={`cupa-player-${index}-consent-${c.key}`}
                  type="checkbox"
                  checked={player.consents[c.key]}
                  onChange={toggleConsent(c.key)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#CCFF00]"
                />
                {c.label}
              </label>
            ))}
          </div>

          <div
            data-testid={`cupa-player-${index}-participation-block`}
            className="mt-6 rounded-2xl border border-neon/40 bg-neon/[0.06] p-5"
          >
            <p className="font-display text-xs font-black uppercase tracking-[0.18em] text-neon">
              Angajament de participare
            </p>
            <p className="mt-3 text-xs leading-relaxed text-neutral-300">
              {PARTICIPATION_TEXT}
            </p>
            <label className="mt-4 flex cursor-pointer items-start gap-3 text-xs font-bold leading-relaxed text-white">
              <input
                data-testid={`cupa-player-${index}-consent-participation`}
                type="checkbox"
                checked={player.consents.participation}
                onChange={toggleConsent("participation")}
                className="mt-0.5 h-4 w-4 shrink-0 accent-[#CCFF00]"
              />
              Îmi asum angajamentul de participare la competiție
            </label>
          </div>

          <div className="mt-7">
            <SignaturePad
              testid={`cupa-player-${index}-signature`}
              label="Semnătura jucătorului"
              value={player.signature}
              onChange={setSig("signature")}
            />
          </div>
        </>
      )}

      {paper && (
        <p
          data-testid={`cupa-player-${index}-paper-note`}
          className="mt-6 rounded-xl border-l-2 border-neon bg-carddark p-4 text-xs leading-relaxed text-neutral-400"
        >
          Acordurile și semnătura se completează pe hârtie, pe fișa tipărită a
          acestui jucător.
        </p>
      )}

      {isMinor && (
        <div
          data-testid={`cupa-player-${index}-guardian-block`}
          className="mt-8 rounded-2xl border-l-2 border-flame bg-carddark p-6"
        >
          <p className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-flame">
            Jucător minor · acordul părintelui / reprezentantului legal
          </p>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Nume părinte / tutore</label>
              <input
                data-testid={`cupa-player-${index}-guardian-name`}
                type="text"
                required={!paper}
                value={player.guardian_name}
                onChange={set("guardian_name")}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Calitate</label>
              <input
                data-testid={`cupa-player-${index}-guardian-relation`}
                type="text"
                value={player.guardian_relation}
                onChange={set("guardian_relation")}
                placeholder="Mamă / Tată / Tutore"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Telefon părinte</label>
              <input
                data-testid={`cupa-player-${index}-guardian-phone`}
                type="tel"
                required={!paper}
                value={player.guardian_phone}
                onChange={set("guardian_phone")}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>CNP părinte</label>
              <input
                data-testid={`cupa-player-${index}-guardian-cnp`}
                type="text"
                inputMode="numeric"
                maxLength={13}
                value={player.guardian_cnp}
                onChange={set("guardian_cnp")}
                className={inputCls}
              />
            </div>
          </div>
          <p className="mt-5 text-xs leading-relaxed text-neutral-400">
            În calitate de părinte/reprezentant legal, îmi exprim acordul ca
            minorul să participe la competiție, confirm că este apt medical și
            îmi asum responsabilitatea pentru participarea acestuia.
          </p>
          <div className="mt-6">
            {paper ? (
              <p className="rounded-xl border-l-2 border-flame bg-surface p-4 text-xs leading-relaxed text-neutral-400">
                Acordul și semnătura părintelui se completează pe hârtie, pe fișa
                tipărită a acestui jucător.
              </p>
            ) : (
              <SignaturePad
                testid={`cupa-player-${index}-guardian-signature`}
                label="Semnătura părintelui / tutorelui"
                value={player.guardian_signature}
                onChange={setSig("guardian_signature")}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const ScanUploader = ({ regId }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/cupa/registrations/${regId}/scans`)
      .then(({ data }) => setUploaded(data.items))
      .catch(() => {});
  }, [regId]);

  const send = async () => {
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await axios.post(`${API}/cupa/registrations/${regId}/scans`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.uploaded} fișier(e) încărcate`);
      setFiles([]);
      const { data: list } = await axios.get(`${API}/cupa/registrations/${regId}/scans`);
      setUploaded(list.items);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Încărcarea a eșuat. Încearcă din nou.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      data-testid="cupa-scan-uploader"
      className="mt-10 rounded-2xl border border-white/10 bg-carddark p-6 text-left"
    >
      <p className="flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-neon">
        <Upload className="h-3.5 w-3.5" /> Trimite formularele semnate
      </p>
      <p className="mt-4 text-xs leading-relaxed text-neutral-400">
        După ce printați și semnați fișele (inclusiv acordul părinților), fotografiați-le
        sau scanați-le și încărcați-le aici. Acceptăm JPG, PNG, WEBP, HEIC și PDF,
        maximum 15 MB per fișier. Le poți trimite și pe rând, în mai multe zile —
        păstrează linkul acestei pagini sau codul înscrierii.
      </p>
      <input
        data-testid="cupa-scan-input"
        type="file"
        multiple
        accept="image/*,application/pdf"
        onChange={(e) => setFiles(Array.from(e.target.files || []))}
        className="mt-5 block w-full cursor-pointer rounded-xl border border-white/10 bg-surface p-3 text-xs text-neutral-300 file:mr-4 file:rounded-full file:border-0 file:bg-neon file:px-4 file:py-2 file:font-display file:text-[11px] file:font-bold file:uppercase file:tracking-wider file:text-canvas"
      />
      {files.length > 0 && (
        <p data-testid="cupa-scan-selected" className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
          {files.length} fișier(e) selectate
        </p>
      )}
      <button
        type="button"
        data-testid="cupa-scan-upload-button"
        onClick={send}
        disabled={uploading || !files.length}
        className="mt-5 flex items-center justify-center gap-2 rounded-full bg-neon px-6 py-3 font-display text-xs font-bold uppercase tracking-wider text-canvas transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
      >
        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        Încarcă scanurile
      </button>
      {uploaded.length > 0 && (
        <ul data-testid="cupa-scan-list" className="mt-6 space-y-2">
          {uploaded.map((f) => (
            <li key={f.id} className="flex items-center gap-2 text-xs text-neutral-400">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-neon" />
              {f.original_filename}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default function CupaInscriere() {
  const [status, setStatus] = useState("loading");
  const [mode, setMode] = useState("online");
  const [team, setTeam] = useState({
    team_name: "",
    captain_name: "",
    captain_phone: "",
    captain_email: "",
    captain_signature: "",
    notes: "",
  });
  const [players, setPlayers] = useState([emptyPlayer()]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/cupa/registration-status`)
      .then(({ data }) => setStatus(data.is_open ? "open" : "closed"))
      .catch(() => setStatus("closed"));
  }, []);

  const setTeamField = (key) => (e) => setTeam((t) => ({ ...t, [key]: e.target.value }));

  const updatePlayer = (i, next) =>
    setPlayers((ps) => ps.map((p, idx) => (idx === i ? next : p)));
  const removePlayer = (i) => setPlayers((ps) => ps.filter((_, idx) => idx !== i));
  const addPlayer = () => setPlayers((ps) => [...ps, emptyPlayer()]);

  const problems = useMemo(() => {
    if (mode === "hartie") return [];
    const list = [];
    if (!team.captain_signature) list.push("Semnătura căpitanului este obligatorie.");
    players.forEach((p, i) => {
      const age = ageFrom(p.birth_date);
      if (!p.signature) list.push(`Jucătorul ${i + 1}: semnătura lipsește.`);
      if (!p.consents.participation)
        list.push(`Jucătorul ${i + 1}: angajamentul de participare trebuie bifat.`);
      if (!Object.values(p.consents).every(Boolean))
        list.push(`Jucătorul ${i + 1}: toate acordurile trebuie bifate.`);
      if (age !== null && age < 18 && (!p.guardian_name || !p.guardian_phone || !p.guardian_signature))
        list.push(`Jucătorul ${i + 1} (minor): datele și semnătura părintelui sunt obligatorii.`);
    });
    return list;
  }, [team.captain_signature, players, mode]);

  const submit = async (e) => {
    e.preventDefault();
    if (problems.length) {
      toast.error(problems[0]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/cupa/registrations`, {
        ...team,
        signature_mode: mode,
        captain_signature: team.captain_signature || null,
        notes: team.notes.trim() || null,
        players: players.map((p) => ({
          ...p,
          signature: p.signature || null,
          phone: p.phone.trim() || null,
          email: p.email.trim() || null,
          id_series: p.id_series.trim() || null,
          cnp: p.cnp.trim() || null,
          guardian_name: p.guardian_name.trim() || null,
          guardian_relation: p.guardian_relation.trim() || null,
          guardian_phone: p.guardian_phone.trim() || null,
          guardian_cnp: p.guardian_cnp.trim() || null,
          guardian_signature: p.guardian_signature || null,
        })),
      });
      setDone(data);
      toast.success("Înscrierea a fost trimisă și semnată electronic!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Trimiterea a eșuat. Verifică datele.");
    } finally {
      setLoading(false);
    }
  };

  if (status === "loading") {
    return (
      <div className="flex min-h-[70svh] items-center justify-center text-neutral-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (status === "closed") return <ClosedNotice />;

  if (done) {
    return (
      <div className="mx-auto flex min-h-[70svh] max-w-2xl items-center px-4">
        <div
          data-testid="cupa-registration-success"
          className="w-full rounded-3xl border border-neon/30 bg-surface p-10 text-center"
        >
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-carddark text-neon">
            <CheckCircle2 className="h-6 w-6" />
          </span>
          <h1 className="mt-6 font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">
            Înscriere trimisă
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-neutral-400">
            Am înregistrat echipa <strong className="text-white">{team.team_name}</strong> cu{" "}
            {done.players} jucători și toate semnăturile electronice. Te contactăm
            pe email sau telefon cu detaliile turneului.
          </p>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            Cod înscriere: {done.id}
          </p>
          {mode === "hartie" && (
            <>
              <a
                href={`${API}/cupa/registrations/${done.id}/pack`}
                data-testid="cupa-download-pack-link"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-neon px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-canvas transition-colors hover:bg-white"
              >
                <Printer className="h-4 w-4" /> Descarcă fișele pentru printare
              </a>
              <ScanUploader regId={done.id} />
            </>
          )}
          <Link
            to="/cupa-caritabila"
            data-testid="cupa-success-back-link"
            className="mt-8 inline-flex rounded-full bg-neon px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-canvas transition-colors hover:bg-white"
          >
            Înapoi la pagina cupei
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 pb-28 pt-36 sm:px-8 lg:px-12">
      <FadeUp>
        <Overline>Formular oficial de înscriere</Overline>
        <h1
          data-testid="cupa-inscriere-title"
          className="mt-4 font-display text-3xl font-black uppercase leading-[1.02] tracking-tight text-white sm:text-4xl lg:text-5xl"
        >
          Înscriere echipă <span className="text-neon">cu semnătură electronică</span>
        </h1>
        <p className="mt-6 text-base leading-relaxed text-neutral-400">
          Completează datele echipei și ale fiecărui jucător, bifează acordurile
          și semnați electronic. Documentele (declarație pe proprie răspundere,
          acord de înscriere, GDPR) se generează automat pentru fiecare
          participant.
        </p>
        <p className="mt-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neon">
          <ShieldCheck className="h-3.5 w-3.5" /> Datele sunt vizibile doar organizatorilor
        </p>
      </FadeUp>

      <FadeUp delay={0.1} className="mt-10">
        <div
          data-testid="cupa-participation-notice"
          className="rounded-3xl border border-neon/40 bg-neon/[0.06] p-6 sm:p-8"
        >
          <p className="font-display text-sm font-black uppercase tracking-[0.18em] text-neon">
            Angajament de participare
          </p>
          <p className="mt-3 text-sm leading-relaxed text-neutral-300">
            {PARTICIPATION_TEXT}
          </p>
          <p className="mt-3 text-xs leading-relaxed text-neutral-400">
            Competiția se joacă în Alba Iulia. Terenul exact și data sunt în curs
            de stabilire și le comunicăm direct echipelor înscrise.
          </p>
        </div>
      </FadeUp>

      <FadeUp delay={0.15} className="mt-6">
        <div className="rounded-3xl border border-white/10 bg-carddark p-6 sm:p-8">
          <p className="font-display text-sm font-bold uppercase tracking-wide text-white">
            Documentele pe care le semnezi
          </p>
          <LegalAccordion />
          <Link
            to="/termeni-si-conditii"
            data-testid="cupa-legal-full-link"
            className="mt-5 inline-block font-mono text-[10px] uppercase tracking-[0.2em] text-neon hover:underline"
          >
            Deschide textele complete într-o pagină separată
          </Link>
        </div>
      </FadeUp>

      <form data-testid="cupa-registration-form" onSubmit={submit} className="mt-12">
        <FadeUp>
          <div className="rounded-3xl border border-white/10 bg-surface p-6 sm:p-8">
            <h2 className="font-display text-base font-black uppercase tracking-wide text-white">
              Cum semnați?
            </h2>
            <div data-testid="cupa-signature-mode" className="mt-4 flex flex-wrap gap-2">
              {[
                { id: "online", label: "Semnăm online, acum", testid: "cupa-mode-online" },
                { id: "hartie", label: "Printăm și semnăm pe hârtie", testid: "cupa-mode-hartie" },
              ].map((m) => (
                <button
                  key={m.id}
                  type="button"
                  data-testid={m.testid}
                  onClick={() => setMode(m.id)}
                  className={`rounded-full px-5 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] transition-colors duration-300 ${
                    mode === m.id
                      ? "bg-neon text-canvas"
                      : "border border-white/15 text-neutral-400 hover:border-neon hover:text-neon"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <p className="mt-4 text-xs leading-relaxed text-neutral-400">
              {mode === "online"
                ? "Fiecare jucător bifează acordurile și semnează electronic, direct în formular."
                : "Completezi doar datele jucătorilor, iar la final descarci un PDF cu fișa fiecăruia (inclusiv acordul părinților). Le printați, le semnați pe hârtie și le scanați sau le predați organizatorilor — util pentru echipe mari, care nu pot semna toți deodată."}
            </p>
          </div>
        </FadeUp>

        <FadeUp className="mt-6">
          <div className="rounded-3xl border border-white/10 bg-surface p-6 sm:p-8">
            <h2 className="font-display text-base font-black uppercase tracking-wide text-white">
              Datele echipei
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className={labelCls}>Numele echipei</label>
                <input
                  data-testid="cupa-team-name"
                  type="text"
                  required
                  value={team.team_name}
                  onChange={setTeamField("team_name")}
                  placeholder="Ex: FC Alba"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Căpitan / persoană de contact</label>
                <input
                  data-testid="cupa-captain-name"
                  type="text"
                  required
                  value={team.captain_name}
                  onChange={setTeamField("captain_name")}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Telefon căpitan</label>
                <input
                  data-testid="cupa-captain-phone"
                  type="tel"
                  required
                  value={team.captain_phone}
                  onChange={setTeamField("captain_phone")}
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Email căpitan</label>
                <input
                  data-testid="cupa-captain-email"
                  type="email"
                  required
                  value={team.captain_email}
                  onChange={setTeamField("captain_email")}
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Observații (opțional)</label>
                <textarea
                  data-testid="cupa-team-notes"
                  rows={3}
                  maxLength={1500}
                  value={team.notes}
                  onChange={setTeamField("notes")}
                  className={`${inputCls} resize-none`}
                />
              </div>
            </div>
            <div className="mt-7">
              {mode === "online" ? (
                <SignaturePad
                  testid="cupa-captain-signature"
                  label="Semnătura căpitanului (în numele echipei)"
                  value={team.captain_signature}
                  onChange={(v) => setTeam((t) => ({ ...t, captain_signature: v }))}
                />
              ) : (
                <p className="rounded-xl border-l-2 border-neon bg-carddark p-4 text-xs leading-relaxed text-neutral-400">
                  Semnătura căpitanului se aplică pe hârtie, pe fișele tipărite.
                </p>
              )}
            </div>
          </div>
        </FadeUp>

        <div className="mt-8 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-display text-base font-black uppercase tracking-wide text-white">
            <Users className="h-4 w-4 text-neon" /> Jucători ({players.length})
          </h2>
          <button
            type="button"
            data-testid="cupa-add-player"
            onClick={addPlayer}
            className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-neon hover:text-neon"
          >
            <Plus className="h-3.5 w-3.5" /> Adaugă jucător
          </button>
        </div>

        <div className="mt-6 space-y-6">
          {players.map((p, i) => (
            <PlayerCard
              key={i}
              index={i}
              player={p}
              mode={mode}
              onChange={updatePlayer}
              onRemove={removePlayer}
              canRemove={players.length > 1}
            />
          ))}
        </div>

        {problems.length > 0 && (
          <ul
            data-testid="cupa-validation-list"
            className="mt-8 space-y-2 rounded-2xl border-l-2 border-flame bg-surface p-5"
          >
            {problems.slice(0, 5).map((p) => (
              <li key={p} className="text-xs leading-relaxed text-neutral-300">
                {p}
              </li>
            ))}
          </ul>
        )}

        <button
          type="submit"
          data-testid="cupa-registration-submit"
          disabled={loading}
          className="mt-10 flex w-full items-center justify-center gap-2 rounded-full bg-neon px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-canvas transition-colors duration-300 hover:bg-white disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Se trimite...
            </>
          ) : (
            <>
              Trimite înscrierea semnată <Send className="h-4 w-4" />
            </>
          )}
        </button>
      </form>
    </div>
  );
}
